// React
import React, {useState, useEffect, useContext, useRef, useCallback, createContext} from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
// MUI
import { AppBar, Box, Toolbar, Typography, Drawer } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
//
import LanguageSwitcher from '/@/app/LanguageSwitcher';
import { MessageStack } from '/@/app/components/MessageStack';
import { DrawerContent } from '/@/app/DrawerContent';
import { AppContext } from '/@/app/AppContext';
import { route, routeAttrs } from '/@/app/routes';
import { saveScrollToLocalStorage, getScrollFromLocalStorage } from '/@/app/scrolls';
import { CurrentPanelBar } from '/@/app/components/CurrentPanelBar';
import { userFlowState } from '/@/app/states/userFlowState';
import { SubscriptionIcon } from "/@/app/components/subscription/SubscriptionIcon.jsx";


const DRAWER_WIDTH = 240;

const ProfileMark = () => {
    const { versionData } = useContext(AppContext);
    switch (versionData.profile) {
        case 'PROD':
            return <></>;
        case 'STAGE':
            return <Box component="span" sx={{ color: 'white', ml: 3 }}>🧪 {versionData.profile}</Box>;
        case 'DEV':
            return <Box component="span" sx={{ color: 'white', ml: 3 }}>🛠 {versionData.profile}</Box>;
        default:
            return <Box component="span" sx={{ color: 'red' }}> Unknown profile: `{versionData.profile}`, please fix</Box>;
    }
}


//
// Show stripes for Stage & Dev.
// Colors should be similar as in PROD, but STAGE & PROD should be distinguishable.
//
const ProfileMarkOverlay = () => {
    const theme = useTheme();
    const {versionData} = useContext(AppContext);

    let colorsSx = {};
    switch (versionData.profile) {
        case 'PROD':
            break;
        case 'STAGE':
            colorsSx = theme.custom.stage.appBar;
            break;
        case 'DEV':
            colorsSx = theme.custom.dev.appBar;
            break;
        default:
            colorsSx.background = "red";  // invalid profile name
    }
    return <Box sx={{...colorsSx, position: 'absolute', left: 0, top: 0, right: 0, bottom: 0}}/>
}

export const ScrollContext = createContext(null);

export const AppLayout = () => {
    const theme = useTheme();
    //
    // Drawer triggers: sidebar form on big screens, drawer form on small
    //
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    const closeDrawer = () => {
        if (isMobile) {
            setMobileOpen(false);
        }
    }

    //
    // Navigation
    //
    const location = useLocation();
    const navigate = useNavigate();
    const onHomeClick = () => {
        navigate(route("/"));
    }

    //
    // Saving scroll positions for each view.
    //
    const mainViewportRef = React.useRef(null);
    const appContext = useContext(AppContext);
    appContext.mainViewportRef = mainViewportRef;  // FIXME use global MobX state for it.

    const { bootstrapData } = useContext(AppContext);
    const bottomExtraPadding = !(bootstrapData.panel.is_latest && bootstrapData.panel.is_editable);
    appContext.bottomExtraPadding = bottomExtraPadding;  // FIXME use global MobX state for it.

const scrollRestoredForRef = useRef(null); // prevents saving scroll BEFORE it's restored.
const saveScroll = () =>
{
	if (scrollRestoredForRef.current === location.key) {
	    saveScrollToLocalStorage(location.pathname, mainViewportRef.current.scrollTop);
	};
}

const restoreScroll = useCallback((pos) =>
{
	const el = mainViewportRef.current;
	if (!el) return;
	if (scrollRestoredForRef.current === location.key) return;

	if (pos===undefined) pos = getScrollFromLocalStorage(location.pathname);

	const maxScroll = el.scrollHeight - el.clientHeight;
	if (pos>maxScroll) pos=maxScroll;

	el.scrollTop = pos;
	scrollRestoredForRef.current = location.key;
}, [location.key, location.pathname]);

    let drawerAvailable = !routeAttrs(location)?._hideDrawer || null;  // location not in route, will be redirected to '/'
    let drawerWidth = drawerAvailable ? DRAWER_WIDTH : 0;

    return (
        <>
            <Box sx={{ display: 'flex', height:'100dvh', flexDirection:'column' }}>
                <AppBar
                    position="sticky"
                    sx={{
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        height: theme.custom.appBarHeight,
                    }}
                >
                    <ProfileMarkOverlay />
                    <Toolbar>
                        {drawerAvailable &&
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{
                                    lineHeight: 0,
                                    mr: 2,
                                    ...(isMobile && { display: 'block' }),
                                    transform: mobileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: theme.transitions.create(
                                        'transform', {
                                            duration: theme.transitions.duration.short,
                                        })
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        }
                        <Typography variant="h6" noWrap component="div">
                            <Box component="span" onClick={onHomeClick}>{isMobile?'NE':'Nestarenie.Expert'}</Box>
                            <SubscriptionIcon />
                            <ProfileMark />
                        </Typography>
                        <LanguageSwitcher/>
	    		{window.appConfig.backend_url &&<Typography variant="body2" sx={{ml:2}} noWrap component="span">{window.appConfig.backend_url}</Typography>}
			{bootstrapData.user.debug_mode && (
			        <Typography
			        variant="h6"
			        noWrap
			        component={Link}
			        to="/cabinet/profile"
			        sx={{
			    	    marginLeft: "auto",
			    		    color: "red",
			    		    backgroundColor: "yellow",
			    		    padding: "2px 6px",
			    		    borderRadius: "4px",
			    		    textDecoration: "none",
			    		    "&:hover": { textDecoration: "underline" }
			        }}
			        >
			        {bootstrapData.user.email}
			        </Typography>
			)}

                    </Toolbar>
                </AppBar>

                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <Box component="nav" sx={{ overflowY: 'auto', overflowX: 'hidden', height: '100%' }}>
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        sx={{
                            display: { xs: 'block', sm: 'none' },
                            zIndex: (theme) => theme.zIndex.drawer,
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: drawerWidth,
                                marginTop: (theme) => theme.custom.appBarHeight,
                                position: 'relative',
                                height: `calc(100% - ${(theme) => theme.custom.appBarHeight})`,
                            },
                        }}
                    >
                        <DrawerContent onItemClick={closeDrawer} userFlowState={userFlowState} />
                    </Drawer>
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', sm: 'block' },

                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: drawerWidth,
                                zIndex: (theme) => theme.zIndex.drawer - 2,
                                position: 'relative',
                                height: `calc(100% - ${(theme) => theme.custom.appBarHeight})`,
                            },
                        }}
                        open
                    >
                        <DrawerContent userFlowState={userFlowState} />
                    </Drawer>
                </Box>
                <Box
                    component="main"
                    ref={mainViewportRef}
                    onScroll={saveScroll}
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        pb: bottomExtraPadding?10:3, // keep space for the floating bottom items, so they don't overlap the content
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        height: '100%',
                        overflowY: 'auto', // make it main scrollable element
                        overflowX: 'hidden',
                    }}>
                    <MessageStack />
                    <ScrollContext.Provider value={{ restoreScroll}}>
                    <Outlet/>
                    </ScrollContext.Provider>

                </Box>
                </Box>
                <CurrentPanelBar />
            </Box>
        </>
    );
}
